<template>
  <div
    v-if="affiliateCustomizations"
    id="embeddedPortal"
    class="embedded grid-lg-aside"
    :style="appStyleProps">
    <LayoutBasePortal />
    <EmbeddedPortalNav />
    <AppNav
      v-if="currentApp.type === 'sba-complete'"
      :portal="false"
      :hide-app-cta="true"
      class="portal-app-nav"
    />
    <div data-smooth-scroll="main" class="embedded-content-container">
      <section class="embedded-content" :class="{ 'embedded-intelligent-content': currentApp.type === 'marketplace' }">
        <slot />
      </section>
      <EmbeddedFooter />
    </div>
    <ChatBot v-if="chatEnabled"/>
  </div>
  <div v-else></div>
</template>

<script>
import { mapState } from 'pinia'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useApprovalsStore } from '~/store/approvals'
import { useBorrowerStore } from '~/store/borrower'
import { useBorrowerUserActionStore } from '~/store/borrower-user-actions'
import { useContractsStore } from '~/store/contracts'
import { useDealsStore } from '~/store/deals'
import { useDocumentsStore } from '~/store/documents'
import { useMatchingStore } from '~/store/matching'
import { useOffersStore } from '~/store/offers'
import { useOptInsStore } from '~/store/opt-ins'
import { useProgressStore } from '~/store/progress'
import { useUserStore } from '~/store/user'
import ChatBot from '~/components/ChatBot'
import EmbeddedFooter from '~/components/embedded/EmbeddedFooter'
import EmbeddedPortalNav from '~/components/embedded/EmbeddedPortalNav'
import AppNav from "~/components/AppNav";
import LayoutBasePortal from './base-portal'

export default {
  name: 'embedded-portal',
  components: {
    EmbeddedFooter,
    LayoutBasePortal,
    EmbeddedPortalNav,
    AppNav,
    ChatBot,
  },
  async setup() {
    const borrowerStore = useBorrowerStore()
    const userStore = useUserStore()
    const matchingStore = useMatchingStore()
    const approvalsStore = useApprovalsStore()
    const documentsStore = useDocumentsStore()
    const dealsStore = useDealsStore()
    const borrowerUserActionStore = useBorrowerUserActionStore()
    const optInsStore = useOptInsStore()
    const offersStore = useOffersStore()
    const contractsStore = useContractsStore()
    const progressStore = useProgressStore()

    const { pending } = await useLazyAsyncData('embedded-portal-layout-setup', async () => {
      await userStore.loadFreshLogin()
      const borrower = await borrowerStore.getBorrower({ forceReload: false })
      await borrowerStore.getBorrowerValues({ forceReload: true })
      await borrowerStore.getLenderUser()
      await matchingStore.getMatches()
      await approvalsStore.requestApprovals()
      await approvalsStore.getContractRequestsByBorrowerId(borrower.id)
      await documentsStore.getDocuments()
      await dealsStore.getDealsWithOffers()
      await borrowerUserActionStore.getActionItems(true)
      await optInsStore.getOptInOffers(true)
      await offersStore.setupOffersDisplay()
      await contractsStore.getContracts()
      await progressStore.fetchBorrowerApplicationProgression()
      return true
    })
    return { pending }
  },
  unmounted() {
    // Let the parent window navigate normally.
    window.onpopstate = () => {}
  },
  computed: {
    ...mapState(useProgressStore, ['currentApp']),
    ...mapState(useAffiliateCustomizationStore, [
      'appStyleProps',
      'affiliateCustomizations',
      'chatEnabled',
    ]),
  },
}
</script>

<style lang="scss">
.embedded-content-container {
  max-width: 800px;
}
#embeddedView,
#embeddedBase,
#embeddedPortal {
  @include affiliateCustomizedApp();
}
.mp-transition-enter-active {
  left: 100%;
  opacity: 0;
  transition: left 0.25s ease-out, opacity 0.15s ease-out;
}
.mp-transition-enter-active {
  position: relative;
}
.mp-transition-enter-to {
  opacity: 1;
  left: 0%;
}
.mp-transition-back-enter-active {
  right: 100%;
  opacity: 0;
  transition: right 0.25s ease-out, opacity 0.15s ease-out;
}
.mp-transition-back-enter-active {
  position: relative;
  @include media-breakpoint-up(lg) {
    right: 10%;
  }
}
.mp-transition-back-enter-to {
  opacity: 1;
  right: 0%;
}
.mp-transition-leave, .mp-transition-back-leave {
  opacity: 1;
}
.mp-transition-leave-active, .mp-transition-back-leave-active {
  transition: opacity 0.15s linear;
}
.mp-transition-leave-to, .mp-transition-back-leave-to {
  opacity: 0;
}
.embedded-content-container {
  max-width: 1200px;
}
// this fixes layout issues on intelligent lending app when embedded-content-container class disappear after client initialization.
// it can be deleted when the root cause of hydration problem would be found
div:not(.embedded-content-container):has(> .embedded-intelligent-content) {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  max-width: 1200px;
}
</style>
